import { ChangeEvent, ClipboardEvent } from 'react'
import { FieldValues } from 'react-hook-form'

import { FormTextInput, FormTextInputProps } from 'Components/Inputs/FormTextInput'
import { isAmount, normalizePastedAmount, normalizeTypedAmount } from 'Utils/helpers/currency'

type AmountInputOptions = {
  currencySymbol?: string
  allowNegative?: boolean
}

const pasteLikeInputTypes = ['insertFromPaste', 'insertFromDrop', 'insertReplacementText']

const isPasteLike = (event: ChangeEvent<HTMLInputElement>) => {
  const { inputType, data } = event.nativeEvent as InputEvent
  if (inputType === 'insertText') return (data?.length ?? 0) > 1
  return pasteLikeInputTypes.includes(inputType)
}

const withCurrencySymbol = (amount: string, currencySymbol: string) =>
  currencySymbol ? `${currencySymbol} ${amount}` : amount

export const normalizeAmountInput = (
  event: ChangeEvent<HTMLInputElement>,
  { currencySymbol = '', allowNegative = false }: AmountInputOptions = {}
) => {
  const value = event.target.value.split(currencySymbol).join('')
  const amount = isPasteLike(event)
    ? normalizePastedAmount(value, allowNegative)
    : normalizeTypedAmount(value, allowNegative)
  return withCurrencySymbol(amount, currencySymbol)
}

export const amountFromPaste = (
  pastedText: string,
  { currencySymbol = '', allowNegative = false }: AmountInputOptions = {}
) =>
  isAmount(pastedText)
    ? withCurrencySymbol(normalizePastedAmount(pastedText, allowNegative), currencySymbol)
    : undefined

export const pasteAmountInto =
  (setValue: (value: string) => void, options?: AmountInputOptions) =>
  (event: ClipboardEvent<HTMLInputElement>) => {
    const value = amountFromPaste(event.clipboardData.getData('text'), options)
    if (value === undefined) return

    event.preventDefault()
    setValue(value)
  }

type FormAmountInputProps<
  TFormValues extends FieldValues,
  TFieldName extends keyof TFormValues
> = Omit<
  FormTextInputProps<TFormValues, TFieldName>,
  'defaultValue' | 'normalizeValue' | 'valueFromPaste'
> & {
  currencySymbol: string
  allowNegative?: boolean
}

export const FormAmountInput = <
  TFormValues extends FieldValues,
  TFieldName extends keyof TFormValues
>({
  currencySymbol,
  allowNegative,
  ...props
}: FormAmountInputProps<TFormValues, TFieldName>) => (
  <FormTextInput
    {...props}
    defaultValue={`${currencySymbol} `}
    normalizeValue={(event) => normalizeAmountInput(event, { currencySymbol, allowNegative })}
    valueFromPaste={(pastedText) => amountFromPaste(pastedText, { currencySymbol, allowNegative })}
  />
)
